import type { ColumnDef } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ComboBox } from "~/components/common/combo-box";
import { SimpleTable } from "~/components/common/simple-table";
import { SearchHeader } from "~/components/common/table-components";
import { Button, Input } from "~/components/ui";
import type { SpotIoDto } from "~/types/api";
import type {
  FormOption,
  NodeAndSensorFormOption,
  SensorOptionType,
} from "~/types/ui";
import { defaultTableColumns } from "~/utils/table-util";

export default function SpotIOFormField({
  value,
  onChange,
  options,
  deveui,
}: {
  value: SpotIoDto[];
  onChange: (val: SpotIoDto[]) => void;
  options: NodeAndSensorFormOption[];
  deveui: string;
}) {
  const { t } = useTranslation();

  async function handleAdd(SpotIo: SpotIoDto) {
    onChange(value.concat(SpotIo));
  }

  async function handleRemove(spotIos: SpotIoDto[]) {
    const removingKeys = spotIos.map((x) => x.key);
    const modifiedValue = value.filter((x) => !removingKeys.includes(x.key));
    onChange(modifiedValue);
  }

  const colDef: ColumnDef<any>[] = [
    defaultTableColumns["select"],
    {
      id: "key",
      accessorKey: "key",
      header: ({ column }) => (
        <SearchHeader column={column} child="tag.fields.spotIoKey" />
      ),
      cell: (info) => info.getValue(),
    },
    {
      id: "inheritedDeveui",
      accessorKey: "inheritedDeveui",
      header: ({ column }) => (
        <SearchHeader column={column} child="tag.fields.deveui" />
      ),
      cell: (info) => info.getValue(),
    },
    {
      id: "inheritedKey",
      accessorKey: "inheritedKey",
      header: ({ column }) => (
        <SearchHeader column={column} child="tagKey.fields.key" />
      ),
      cell: (info) => info.getValue(),
    },
  ];

  return (
    <>
      <SpotIoCreate options={options} onAdd={handleAdd} tagId={deveui} />
      <SimpleTable
        data={value}
        colDef={colDef}
        pageSizes={[10, 25]}
        onRemove={handleRemove}
        removable
      />
    </>
  );
}

export function SpotIoCreate({
  options,
  onAdd,
  tagId,
}: {
  options: NodeAndSensorFormOption[];
  onAdd: (val: SpotIoDto) => void;
  tagId: string;
}) {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [selectedSensor, setSelectedSensor] = useState<string>("");
  const [spotioKey, setSpotioKey] = useState<string>("");

  const sensorOptions = useMemo(() => {
    const sensors = options.find((x) => x.label == "sensors")
      ?.value as SensorOptionType[];

    return sensors.filter((x) => x.deveui == selectedTag).map((x) => x.tagKey);
  }, [selectedTag, options]);

  async function handleTagSelect(value: string | string[]) {
    setSelectedTag(value as string);
    setSelectedSensor("");
  }

  async function handleAdd() {
    if (selectedTag && selectedSensor && spotioKey) {
      onAdd({
        tagId,
        inheritedDeveui: selectedTag,
        inheritedKey: selectedSensor,
        key: spotioKey,
      });
      setSpotioKey("");
      await handleTagSelect("");
      return;
    }
    toast.error(t("common.errors.missingFields"));
  }

  return (
    <div className="flex">
      <div className="mr-2 w-1/4">
        <ComboBox
          options={(
            options?.find((x) => x.label == "tags")
              ?.value as FormOption<string>[]
          ).filter((x) => x.value != tagId)}
          value={selectedTag}
          onChange={handleTagSelect}
          placeholder={t("tag.placeholders.deveui")}
        />
      </div>
      <div className="mr-2 w-1/4">
        <ComboBox
          options={sensorOptions}
          value={selectedSensor}
          onChange={(val) => setSelectedSensor(val as string)}
          placeholder={t("tagKey.placeholders.key")}
          disabled={selectedTag == ""}
        />
      </div>
      <div className="mr-2 w-1/4">
        <Input
          value={spotioKey ?? ""}
          placeholder={t("tag.placeholders.spotIoKey")}
          onChange={(e) => setSpotioKey(e.target.value)}
        />
      </div>
      <div className="w-1/4">
        <Button
          variant="ghost"
          aria-label="Add Selected"
          onClick={handleAdd}
          type="button"
        >
          <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 " />
        </Button>
      </div>
    </div>
  );
}
